import AssignmentIcon from '@mui/icons-material/Assignment';
import {
    Box,
    Typography,
} from '@mui/material';
import React from 'react';
import { LessonPlanSessionModel } from '../../object-actions/types/types';
import { ProgramWeek } from '../growth-report/GrowthReportEnqueueButton';

/** Session tabs: green tones. Survey tabs: amber/orange tones. */
const TAB_COLORS = {
    sessionComplete: '#2e7d32',
    sessionIncomplete: '#c8e6c9',
    sessionCurrentRing: '#1565c0',
    surveyComplete: '#ef6c00',
    surveyIncomplete: '#ffe0b2',
    surveyCurrentRing: '#e65100',
} as const;

/** Survey columns are this fraction of a session column width. */
const SURVEY_COLUMN_FR = 0.45;

const isSurveyWeek = (sessionNumber: number | undefined | null): sessionNumber is ProgramWeek =>
    sessionNumber === 4 || sessionNumber === 8 || sessionNumber === 12;

interface LessonPlanSessionNavProps {
    sessions: LessonPlanSessionModel[];
    activeSessionId: number;
    onSelectSession: (sessionId: number, source: 'dropdown' | 'prev_next' | 'session_nav') => void;
    /** Coach / parent / payer: show week 4/8/12 progress-survey tabs. */
    showSurveyTabs?: boolean;
    completedSurveyWeeks?: Partial<Record<ProgramWeek, boolean>>;
    activeSurveyWeek?: ProgramWeek | null;
    onSelectSurvey?: (week: ProgramWeek) => void;
}

const LessonPlanSessionNav: React.FC<LessonPlanSessionNavProps> = ({
    sessions,
    activeSessionId,
    onSelectSession,
    showSurveyTabs = false,
    completedSurveyWeeks = {},
    activeSurveyWeek = null,
    onSelectSurvey,
}) => {
    const columnTracks: string[] = [];
    for (const session of sessions) {
        columnTracks.push('minmax(0, 1fr)');
        if (showSurveyTabs && isSurveyWeek(session.session_number)) {
            columnTracks.push(`minmax(0, ${SURVEY_COLUMN_FR}fr)`);
        }
    }
    if (columnTracks.length === 0) {
        columnTracks.push('minmax(0, 1fr)');
    }

    return (
        <Box
            component="nav"
            aria-label="Lesson sessions"
            data-testid="lesson-plan-session-nav"
        >
            <Box
                aria-label="Session and survey progress"
                sx={{
                    display: 'grid',
                    gridTemplateColumns: columnTracks.join(' '),
                    gap: 0.5,
                    alignItems: 'end',
                }}
            >
                {sessions.map((session) => {
                    const sessionNumber = session.session_number ?? session.id;
                    const isCurrent = session.id === activeSessionId && activeSurveyWeek == null;
                    const isComplete = Boolean(session.completed_at);
                    const surveyWeek = isSurveyWeek(session.session_number) ? session.session_number : null;
                    const showSurvey = showSurveyTabs && surveyWeek != null;
                    const surveyComplete = showSurvey ? Boolean(completedSurveyWeeks[surveyWeek]) : false;
                    const surveyCurrent = showSurvey && activeSurveyWeek === surveyWeek;
                    const surveyLabel = surveyWeek === 12 ? 'Final survey' : `Week ${surveyWeek} survey`;

                    return (
                        <React.Fragment key={session.id}>
                            <Box
                                component="button"
                                type="button"
                                onClick={() => onSelectSession(session.id, 'session_nav')}
                                aria-label={`Session ${sessionNumber}${isComplete ? ', completed' : ', incomplete'}${isCurrent ? ', current' : ''}`}
                                aria-current={isCurrent ? 'step' : undefined}
                                title={`Session ${sessionNumber}: ${isComplete ? 'complete' : 'incomplete'}`}
                                data-attr={`lesson-plan-session-tab-${sessionNumber}`}
                                data-testid={`lesson-plan-session-tab--${sessionNumber}`}
                                data-complete={isComplete ? 'true' : 'false'}
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    gap: 0.35,
                                    minWidth: 0,
                                    p: 0,
                                    border: 0,
                                    background: 'transparent',
                                    cursor: 'pointer',
                                    '&:focus-visible': {
                                        outline: theme => `2px solid ${theme.palette.primary.main}`,
                                        outlineOffset: 2,
                                        borderRadius: 1,
                                    },
                                }}
                            >
                                <Typography
                                    variant="caption"
                                    sx={{
                                        fontSize: '0.65rem',
                                        lineHeight: 1,
                                        color: isCurrent ? 'primary.main' : 'text.secondary',
                                        fontWeight: isCurrent ? 700 : 500,
                                    }}
                                >
                                    {sessionNumber}
                                </Typography>
                                <Box
                                    sx={{
                                        width: '100%',
                                        height: 8,
                                        borderRadius: 1,
                                        backgroundColor: isComplete
                                            ? TAB_COLORS.sessionComplete
                                            : TAB_COLORS.sessionIncomplete,
                                        boxShadow: isCurrent
                                            ? `inset 0 0 0 2px ${TAB_COLORS.sessionCurrentRing}`
                                            : 'none',
                                        transition: 'transform 160ms ease, background-color 160ms ease',
                                        transform: isCurrent ? 'scaleY(1.25)' : 'none',
                                    }}
                                />
                            </Box>

                            {showSurvey && (
                                <Box
                                    component="button"
                                    type="button"
                                    onClick={() => onSelectSurvey?.(surveyWeek)}
                                    aria-label={`${surveyLabel}${surveyComplete ? ', completed' : ', incomplete'}${surveyCurrent ? ', current' : ''}`}
                                    aria-current={surveyCurrent ? 'step' : undefined}
                                    title={`${surveyLabel}: ${surveyComplete ? 'complete' : 'incomplete'}`}
                                    data-attr={`lesson-plan-survey-tab-${surveyWeek}`}
                                    data-testid={`lesson-plan-survey-tab--${surveyWeek}`}
                                    data-complete={surveyComplete ? 'true' : 'false'}
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        gap: 0.35,
                                        minWidth: 0,
                                        p: 0,
                                        border: 0,
                                        background: 'transparent',
                                        cursor: 'pointer',
                                        '&:focus-visible': {
                                            outline: theme => `2px solid ${theme.palette.warning.main}`,
                                            outlineOffset: 2,
                                            borderRadius: 1,
                                        },
                                    }}
                                >
                                    <AssignmentIcon
                                        sx={{
                                            fontSize: '0.85rem',
                                            color: surveyComplete ? TAB_COLORS.surveyComplete : 'warning.light',
                                        }}
                                    />
                                    <Box
                                        sx={{
                                            width: '100%',
                                            height: 8,
                                            borderRadius: 1,
                                            backgroundColor: surveyComplete
                                                ? TAB_COLORS.surveyComplete
                                                : TAB_COLORS.surveyIncomplete,
                                            boxShadow: surveyCurrent
                                                ? `inset 0 0 0 2px ${TAB_COLORS.surveyCurrentRing}`
                                                : 'none',
                                            transition: 'transform 160ms ease, background-color 160ms ease',
                                            transform: surveyCurrent ? 'scaleY(1.25)' : 'none',
                                        }}
                                    />
                                </Box>
                            )}
                        </React.Fragment>
                    );
                })}
            </Box>
        </Box>
    );
};

export default LessonPlanSessionNav;
